import "./Home.css";
import "bulma/css/bulma.min.css";
import React from "react";
import TrustBand from "../../components/TrustBand/TrustBand";
import HomeHighlights from "./HomeHighlights";

const IOS_URL = "https://apps.apple.com/us/app/range-pocket/id6733222657";
const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.armorysync.rangepocket";

const HOME_TRUST_ITEMS = [
  {
    label: "Your collection, in context",
    body: "Firearms, photos, receipts, accessories, and maintenance history stay connected to the item they belong to.",
  },
  {
    label: "Range history that is useful later",
    body: "Visits, round counts, notes, target photos, and drills are organized so the next session starts with real context.",
  },
  {
    label: "Designed for normal ownership",
    body: "The app is built around practical records owners already keep, without forcing a heavy workflow.",
  },
  {
    label: "Direct product support",
    body: "Questions before downloading or feedback after setup can go straight to the ArmorySync support email.",
  },
];

const TRACK_ITEMS = [
  {
    label: "Firearms",
    body: "Make, model, caliber, photos, value, purchase details, and notes.",
    href: "/features/firearm-inventory",
  },
  {
    label: "Range visits",
    body: "Range, time, firearms used, rounds fired, notes, photos, and drills.",
    href: "/features/range-visit-logging",
  },
  {
    label: "Maintenance",
    body: "Cleaning intervals, maintenance actions, records, and reminders.",
    href: "/firearm-maintenance-tracker",
  },
  {
    label: "Ammo and accessories",
    body: "Inventory, linked accessories, parts, batteries, and ownership cost.",
    href: "/features/accessory-tracking",
  },
  {
    label: "Training",
    body: "Plans, drills, practice notes, categories, and progress over time.",
    href: "/features/training-plans",
  },
  {
    label: "Receipts and photos",
    body: "Useful supporting records for warranty, resale, insurance, and recall context.",
    href: "/features/firearm-inventory",
  },
];

const SEO_SECTIONS = [
  {
    title: "A firearm inventory app owners can understand",
    body:
      "Range Pocket gives responsible firearm owners one place for firearms, ammo, accessories, receipts, photos, maintenance, range visits, and training notes.",
    links: [
      { href: "/range-pocket", label: "Explore Range Pocket" },
      { href: "/features", label: "Range Pocket features" },
      { href: "/faq", label: "Read the FAQ" },
    ],
  },
  {
    title: "Built around normal range routines",
    body:
      "Log sessions, connect firearms to range history, track round counts, save target photos, and review what to work on before the next visit.",
    links: [
      { href: "/features/firearm-inventory", label: "Firearm inventory feature" },
      { href: "/blog/range-pocket-complete-tool", label: "See how it fits a range day" },
      { href: "/range-log-app", label: "Range log app" },
      { href: "/blog/range-pocket-records", label: "How records work" },
    ],
  },
  {
    title: "A company focused on practical firearm software",
    body:
      "ArmorySync is building from Range Pocket first: a useful owner app today, shaped around practical records and range-focused workflows.",
    links: [
      { href: "/about", label: "About ArmorySync" },
      { href: "/blog/range-pocket-records", label: "How records work" },
      { href: "/contact", label: "Contact ArmorySync" },
    ],
  },
];

function App() {
  return (
    <div className="hero">
      <section className="home-product-hero" aria-labelledby="home-product-title">
        <div className="home-product-copy">
          <div className="home-eyebrow">ArmorySync presents Range Pocket</div>
          <h1 id="home-product-title" className="title">
            Firearm inventory and range logs, without the spreadsheet.
          </h1>
          <p className="tagline">
            Range Pocket helps responsible owners organize firearms, ammo,
            accessories, maintenance, range visits, target photos, and training
            history without turning range day into admin work.
          </p>

          <div className="cta-row">
            <a className="cta primary" href={IOS_URL}>
              Download on the App Store
            </a>
            <a className="cta ghost" href={ANDROID_URL}>
              Get it on Google Play
            </a>
            <a className="cta text" href="/range-pocket">
              Explore product
            </a>
          </div>
          <p className="home-support-note">
            Questions before downloading? Email <a href="mailto:support@armorysync.com">support@armorysync.com</a>.
          </p>

          <div className="home-app-proof" aria-label="Range Pocket app screens">
            <span>Range Pocket in use</span>
            <div className="home-app-proof-shots">
              <img src="/features/feature-firearm-list.webp" alt="Range Pocket firearm inventory list" />
              <img src="/features/feature-range-visits.webp" alt="Range Pocket range visit records" />
              <img src="/features/feature-training-overview.webp" alt="Range Pocket training overview" />
            </div>
          </div>
        </div>
        <div className="home-product-media" aria-hidden="true">
          <img src="/features/feature-armory-status.webp" alt="" />
        </div>
      </section>

      <HomeHighlights />

      <TrustBand
        className="home-trust-band"
        eyebrow="Owner-first records"
        title="Built for owners who want a useful record, not a complicated system."
        items={HOME_TRUST_ITEMS}
        ctaLabel="Read the FAQ"
      />

      <section className="home-track-panel" aria-labelledby="home-track-heading">
        <div className="home-track-copy">
          <div className="home-seo-kicker">What you can track</div>
          <h2 id="home-track-heading">One place for the records that follow every range trip.</h2>
          <p>
            Range Pocket is not trying to turn ownership into admin work. It gives you
            structured places for the details you already care about, then makes that
            history easier to review next time.
          </p>
        </div>
        <div className="home-track-grid">
          {TRACK_ITEMS.map((item) => (
            <a className="home-track-card" href={item.href} key={item.label}>
              <strong>{item.label}</strong>
              <span>{item.body}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="home-seo-panel" aria-labelledby="home-seo-heading">
        <div className="home-seo-kicker">Range Pocket by ArmorySync</div>
        <h2 id="home-seo-heading">Built around the way owners actually use their gear.</h2>
        <div className="home-seo-grid">
          {SEO_SECTIONS.map((section) => (
            <article className="home-seo-card" key={section.title}>
              <h3>{section.title}</h3>
              <p>{section.body}</p>
              <div className="home-seo-links">
                {section.links.map((link) => (
                  <a href={link.href} key={link.href}>{link.label}</a>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="home-company-note" aria-labelledby="home-company-heading">
        <div>
          <div className="home-seo-kicker">The company behind it</div>
          <h2 id="home-company-heading">ArmorySync builds practical software around real range routines.</h2>
        </div>
        <p>
          ArmorySync is focused on tools that help firearm owners keep cleaner
          records and make better use of range time. Range Pocket is the app you
          can evaluate today, shaped by the same practical,
          field-tested approach.
        </p>
        <a href="/about">Learn about ArmorySync</a>
      </section>
    </div>
  );
}

export const Home_Page = {
  path: "/home",
  element: <App />,
};
